import { useCallback } from 'react';

import type { JSONValue } from '@savvy/constants/types/json-value';
import { jsonParse } from '@savvy/utils/error-handling/json-parse';

// LocalStorage is not available when rendering on the server side.
function getIsSSR(): boolean {
  return typeof window === 'undefined';
}

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters -- type predicate; `T` only narrows the caller's inferred type
function defaultValidator<T>(_x: unknown): _x is T {
  return true;
}

export function getLocalStorageValue<T = JSONValue>({
  dataKey,
  validator = defaultValidator,
}: {
  readonly dataKey: string;
  readonly validator?: (data: unknown) => data is T;
}): T | null {
  if (getIsSSR()) {
    return null;
  }

  const data = localStorage.getItem(dataKey) ?? '';
  const parsed = jsonParse(data);

  if (parsed.isOk() && validator(parsed.value)) {
    return parsed.value;
  }

  return null;
}

export function setLocalStorageValue<T = JSONValue>({
  dataKey,
  data,
  validator = defaultValidator,
}: {
  readonly dataKey: string;
  readonly data: T;
  readonly validator?: (data: unknown) => data is T;
}): void {
  if (getIsSSR() || !validator(data)) {
    return;
  }

  const stringified = typeof data === 'string' ? data : JSON.stringify(data);
  localStorage.setItem(dataKey, stringified);
}

export function removeLocalStorageValue(dataKey: string): void {
  if (getIsSSR()) {
    return;
  }

  localStorage.removeItem(dataKey);
}

export function useLocalStorage<T = JSONValue>({
  dataKey,
  validator = defaultValidator,
}: {
  readonly dataKey: string;
  readonly validator?: (data: unknown) => data is T;
}): {
  getValue: () => T | null;
  setValue: (data: T) => void;
  removeValue: () => void;
} {
  // Memoize callbacks to avoid re-defining unless the dataKey or validator changes.
  return {
    getValue: useCallback(() => getLocalStorageValue<T>({ dataKey, validator }), [dataKey, validator]),
    setValue: useCallback((data: T) => setLocalStorageValue<T>({ dataKey, validator, data }), [dataKey, validator]),
    removeValue: useCallback(() => removeLocalStorageValue(dataKey), [dataKey]),
  };
}
